/**
 * Shared, framework-agnostic field validators.
 *
 * Added 2026-06-22 in response to QA (Raji): forms collecting
 * email / phone / name accepted anything. Each validator returns a
 * human-readable error string, or `null` when the value is acceptable.
 * Forms collect the first non-null result and surface it inline.
 */
// Practical email shape: local@domain.tld — no spaces, a dot in the
// domain, and a 2+ char TLD. Deliberately not full RFC 5322 (that
// regex is unusable); the backend's @Email is the final gate.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Phone: digits with common separators (+ ( ) - . and spaces).
// We count the digits rather than trust the formatting.
const PHONE_ALLOWED_RE = /^[+()\-.\s\d]+$/;
export function isValidEmail(value) {
    return EMAIL_RE.test(value.trim());
}
export function validateEmail(value, opts = {}) {
    const { required = true, label = 'Email address' } = opts;
    const v = value.trim();
    if (!v)
        return required ? `${label} is required.` : null;
    if (v.length > 254)
        return `${label} is too long.`;
    if (!EMAIL_RE.test(v))
        return `Please enter a valid ${label.toLowerCase()} (e.g. name@company.com).`;
    return null;
}
export function validateName(value, opts = {}) {
    const { required = false, label = 'Name', min = 1, max = 50 } = opts;
    const v = value.trim();
    if (!v)
        return required ? `${label} is required.` : null;
    if (v.length < min)
        return `${label} must be at least ${min} character${min === 1 ? '' : 's'}.`;
    if (v.length > max)
        return `${label} must be ${max} characters or fewer.`;
    // Letters (incl. accented), spaces, hyphens, apostrophes, periods.
    // Blocks digits and other symbols that don't belong in a person's name.
    if (!/^[\p{L}][\p{L}\s'.-]*$/u.test(v)) {
        return `${label} contains invalid characters.`;
    }
    return null;
}
export function validatePhone(value, opts = {}) {
    const { required = false, label = 'Phone' } = opts;
    const v = value.trim();
    if (!v)
        return required ? `${label} is required.` : null;
    if (!PHONE_ALLOWED_RE.test(v)) {
        return `${label} may only contain digits and + ( ) - . characters.`;
    }
    const digits = v.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) {
        return `Please enter a valid ${label.toLowerCase()} (7-15 digits, optionally with a country code).`;
    }
    return null;
}
/** Returns the first error from a list of validator results, or null. */
export function firstError(...results) {
    return results.find((r) => r != null) ?? null;
}
